import { useState } from "react";
import { Sync, Refresh, Info } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Alert,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import httpclient from "../../../Utils";
import { useNotification } from "../../../Hooks/useNotification";
import useTokenRefresh from "../../../Hooks/useTokenRefresh";
import { useApiErrorHandler } from "../../../Hooks/useApiErrorHandler";

const ResyncOrder = () => {
  const [jobId, setJobId] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const { getTokenRefreshed: refresh } = useTokenRefresh();
  const handleApiError = useApiErrorHandler();
  const showNotification = useNotification();

  const handleResync = async () => {
    if (!jobId.trim()) {
      showNotification("error", "Please enter a Job Number");
      return;
    }

    setIsLoading(true);
    try {
      // Make the API call to resync the order
      const response = await httpclient.get(
        `orders/${jobId.trim()}/resync-order`
      );

      if (response.data?.status === 200) {
        showNotification(
          "success",
          response.data?.message || "Order resynced successfully!"
        );

        setJobId(""); // Clear the input after successful resync
      } else {
        showNotification(
          "error",
          response.data?.message || "Failed to resync order"
        );
      }
    } catch (error) {
      console.error("Error during resync:", error);
      if (error.response?.status === 401) {
        refresh();
      } else {
        handleApiError(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleResync();
    }
  };

  return (
    <>
      <Box sx={{ width: "100%", padding: 3 }}>
        <Card
          elevation={3}
          sx={{
            borderRadius: "12px",
            overflow: "hidden",
            border: "1px solid #e0e0e0",
            background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
          }}
        >
          <CardHeader
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Refresh sx={{ color: "#7152F3" }} />
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "#7152F3" }}
                >
                  Fetch Order
                </Typography>
              </Box>
            }
            sx={{
              padding: 4,
              borderBottom: "1px solid #f0f0f0",
              backgroundColor: "#fafbfc",
            }}
          />

          <CardContent sx={{ padding: 4, pt: 6 }}>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="body2"
                sx={{
                  mb: 2,
                  color: "#555",
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Info sx={{ fontSize: "16px", color: "#7152F3" }} />
                Enter the Job Number below to resync the order from SSS Auto ERP
              </Typography>
            </Box>

            {/* {error && (
              <Alert
                severity="error"
                sx={{ mb: 3, borderRadius: "8px" }}
                onClose={() => setError("")}
              >
                {error}
              </Alert>
            )} */}

            <Grid container spacing={3} alignItems="flex-end">
              <Grid item xs={12} md={4}>
                <TextField
                  label="Job Number"
                  variant="outlined"
                  value={jobId}
                  onChange={(e) => setJobId(e.target.value)}
                  onKeyPress={handleKeyPress}
                  fullWidth
                  placeholder="Enter job number (e.g., JOB12345)"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Sync sx={{ color: "#7152F3" }} />
                      </InputAdornment>
                    ),
                    sx: {
                      borderRadius: "8px",
                      "& .MuiOutlinedInput-input": {
                        padding: "12px 14px",
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Tooltip
                  title={
                    jobId.trim()
                      ? "Click to resync this order"
                      : "Enter a job number first"
                  }
                  placement="top"
                >
                  <span>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleResync}
                      disabled={isLoading || !jobId.trim()}
                      startIcon={
                        isLoading ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <Refresh />
                        )
                      }
                      sx={{
                        borderRadius: "8px",
                        height: "48px",
                        fontWeight: 500,
                        textTransform: "none",
                        background:
                          "linear-gradient(135deg, #7152F3 0%, #5c37eeff 100%)",
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #502ddbff 0%, #3a14d1ff 100%)",
                          transform: "translateY(-1px)",
                          boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
                        },
                        "&:disabled": {
                          background: "#ccc",
                          transform: "none",
                          boxShadow: "none",
                        },
                      }}
                    >
                      {isLoading ? "Fetching..." : "Fetch Now"}
                    </Button>
                  </span>
                </Tooltip>
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, pt: 3, borderTop: "1px solid #f0f0f0" }}>
              <Typography
                variant="caption"
                sx={{
                  color: "#888",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Info sx={{ fontSize: "14px" }} />
                This will fetch the latest order data from SSS Auto ERP system
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </>
  );
};

export default ResyncOrder;
